/**
 * Plan 14a, Task 8.1 — gsap timeline registry (manual ticker, render-loop driven)
 *
 * NOTES — manual-ticker pattern chosen:
 *
 *   Each timeline is created with `{ paused: true }` so GSAP's own auto-ticker
 *   never advances it. The registry accumulates elapsed time and drives each
 *   timeline forward by calling `timeline.time(elapsed)` on every `update(dt)`.
 *
 *   Problem: `timeline.time(value)` is clamped to `[0, duration]`. An empty
 *   timeline has duration=0, so `time()` always returns 0.
 *
 *   Solution: inject a zero-side-effect sentinel tween (`gsap.set`) at position
 *   VIRTUAL_DURATION (1 hour = 3600s) to give every registry timeline a virtual
 *   duration large enough for any game session. The sentinel tween animates a
 *   private `_dummyTarget` object that is never used elsewhere, so it has zero
 *   observable side effects on game state.
 *
 *   Alternative rejected: `gsap.ticker.remove(gsap.updateRoot)` + calling
 *   `gsap.updateRoot(totalTime)`. This works but mutates global GSAP state,
 *   interfering with other registries and test isolation (vitest runs all tests
 *   in a single fork with shared module state per vitest.config.ts).
 *
 *   GSAP API references:
 *   - `gsap.core.Animation.time(value)` — seeks the playhead to absolute seconds.
 *     On a paused timeline, fires tween callbacks at the seek point but does NOT
 *     resume playback.
 *   - `gsap.core.Animation.kill()` — stops and removes from parent timeline,
 *     releases for GC.
 */

import gsap from 'gsap';

/** Virtual duration extended via sentinel; covers any game session ≤ 1 hour. */
const VIRTUAL_DURATION = 3600;

/** Private sentinel target — a plain object, never read by game code. */
const _dummyTarget = { _: 0 };

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

export interface GsapTimelineHandle {
  readonly timeline: gsap.core.Timeline;
  dispose(): void;
}

export interface GsapRegistry {
  create(label?: string): GsapTimelineHandle;
  update(deltaSeconds: number): void;
  setPaused?(paused: boolean, opts?: { readonly allowLabelPrefixes?: readonly string[] }): void;
  readonly size: number;
  dispose(): void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Internal state per tracked timeline
// ─────────────────────────────────────────────────────────────────────────────

interface TrackedTimeline {
  handle: GsapTimelineHandle;
  label: string;
  elapsedSeconds: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createGsapRegistry(): GsapRegistry {
  const tracked = new Set<TrackedTimeline>();
  let disposed = false;
  let paused = false;
  let pauseAllowLabelPrefixes: readonly string[] = [];

  function create(label?: string): GsapTimelineHandle {
    // Create timeline in paused state so GSAP's auto-ticker never advances it.
    const tl = gsap.timeline({ paused: true, ...(label ? { id: label } : {}) });

    // Inject a zero-side-effect sentinel at VIRTUAL_DURATION to give the
    // timeline a non-zero duration. Without this, timeline.time(value) is
    // clamped to 0 on an empty timeline (duration=0).
    tl.set(_dummyTarget, { _: 0 }, VIRTUAL_DURATION);

    const entry: TrackedTimeline = { handle: null!, label: label ?? '', elapsedSeconds: 0 };

    let handleDisposed = false;

    const handle: GsapTimelineHandle = {
      get timeline() {
        return tl;
      },
      dispose() {
        if (handleDisposed) return;
        handleDisposed = true;
        tl.kill();
        tracked.delete(entry);
      },
    };

    entry.handle = handle;
    tracked.add(entry);
    return handle;
  }

  function update(deltaSeconds: number): void {
    if (disposed) return;
    for (const entry of tracked) {
      if (paused && !isPauseAllowed(entry.label)) continue;
      entry.elapsedSeconds += deltaSeconds;
      // Seek the paused timeline to the new absolute time.
      entry.handle.timeline.time(entry.elapsedSeconds);
    }
  }

  function isPauseAllowed(label: string): boolean {
    if (!paused) return true;
    return pauseAllowLabelPrefixes.some((prefix) => label.startsWith(prefix));
  }

  function setPaused(
    nextPaused: boolean,
    opts?: { readonly allowLabelPrefixes?: readonly string[] },
  ): void {
    paused = nextPaused;
    pauseAllowLabelPrefixes = nextPaused ? (opts?.allowLabelPrefixes ?? []) : [];
  }

  function dispose(): void {
    if (disposed) return;
    disposed = true;
    for (const entry of tracked) {
      // Mark each handle as disposed to prevent double-kill if caller calls
      // handle.dispose() after registry.dispose().
      entry.handle.dispose();
    }
    // tracked.clear() is implicit: each handle.dispose() calls tracked.delete(entry)
  }

  return {
    create,
    update,
    setPaused,
    get size() {
      return tracked.size;
    },
    dispose,
  };
}
